<template>
  <div class="epc-dropdown-container" v-click-outside="closeDropdown">
    <div
      ref="dropdownGroup"
      class="epc-dropdown-button-group flex"
      :class="{ 'active': isDropdownOpen || isActive, 'arrow-hover': isArrowHovered }">
      <div
        :id="buttonId"
        class="epc-button inactive epc-dropdown-main-button"
        :class="{ 'active': isActive || isDropdownOpen }"
        @click="executeSelectedOption">
        <b-icon pack="fas" :icon="(internalSelectedOption && internalSelectedOption.icon) ? internalSelectedOption.icon : (mainIcon || 'object-group')"></b-icon>
      </div>
      <div
        class="epc-button inactive epc-dropdown-arrow-button"
        :class="{ 'active': isDropdownOpen || isActive }"
        @click="toggleDropdown"
        @mouseenter="onArrowHover"
        @mouseleave="onArrowLeave">
        <b-icon pack="fas" icon="chevron-down" :class="{ 'rotate-180': isDropdownOpen }"></b-icon>
      </div>
    </div>
    <div
      v-if="isDropdownOpen"
      class="epc-dropdown-menu">
      <div
        v-for="option in options"
        :key="option.value"
        class="epc-dropdown-item"
        :class="{ 'active': internalSelectedOption && internalSelectedOption.value === option.value }"
        @click="selectOption(option)">
        <b-icon pack="fas" :icon="option.icon"></b-icon>
        <span class="ml-2">{{ option.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SplitActionDropdown",

  props: {
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedOption: {
      type: Object,
      default: null
    },
    buttonId: {
      type: String,
      default: 'toolbar-dropdown'
    },
    isActive: {
      type: Boolean,
      default: false
    },
    mainIcon: {
      type: String,
      default: null
    },
    defaultOption: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      isDropdownOpen: false,
      isArrowHovered: false,
      internalSelectedOption: null
    }
  },

  watch: {
    selectedOption: {
      immediate: true,
      deep: true,
      handler(newOption) {
        if (newOption) {
          this.internalSelectedOption = { ...newOption };
        } else if (this.defaultOption) {
          this.internalSelectedOption = { ...this.defaultOption };
        } else if (this.options && this.options.length > 0) {
          this.internalSelectedOption = { ...this.options[0] };
        }
      }
    },
    options: {
      immediate: true,
      handler(newOptions) {
        if (newOptions && newOptions.length > 0 && !this.internalSelectedOption) {
          this.internalSelectedOption = this.defaultOption ? { ...this.defaultOption } : { ...newOptions[0] };
        }
      }
    }
  },

  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },

    closeDropdown() {
      this.isDropdownOpen = false;
    },

    onArrowHover() {
      this.isArrowHovered = true;
    },

    onArrowLeave() {
      this.isArrowHovered = false;
    },

    executeSelectedOption() {
      if (!this.internalSelectedOption) return;
      this.$emit('option-selected', this.internalSelectedOption);
    },

    selectOption(option) {
      if (!option) return;
      this.internalSelectedOption = { ...option };
      this.isDropdownOpen = false;
      this.$emit('option-selected', option);
    }
  },

  directives: {
    'click-outside': {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            if (vnode.context && binding.expression) {
              vnode.context[binding.expression]();
            }
          }
        };
        setTimeout(() => {
          document.body.addEventListener('click', el.clickOutsideEvent);
        }, 0);
      },
      unbind(el) {
        if (el.clickOutsideEvent) {
          document.body.removeEventListener('click', el.clickOutsideEvent);
        }
      }
    }
  }
}
</script>

<style>
.epc-dropdown-container {
  position: relative;
  float: left;
  margin: 4px;
  height: auto;
  max-height: 100%;
  overflow: visible;
}

.epc-dropdown-button-group {
  display: flex;
  border-radius: 4px;
  align-items: stretch;
  height: 100%;
}

.epc-dropdown-button-group.active {
  border: 1px solid #00bfff;
}

.epc-dropdown-button-group.active .epc-button {
  border-color: transparent;
}

.epc-dropdown-button-group.active .epc-dropdown-main-button {
  border-right: 1px solid #00bfff;
}

.epc-dropdown-button-group.active .epc-dropdown-arrow-button {
  border: none;
  border-left: 1px solid #00bfff;
}

.epc-dropdown-main-button {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin: 0;
  border-right: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.epc-dropdown-arrow-button {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin: 0;
  padding: 6px 3px;
  min-width: 18px;
  width: 18px;
  border: none;
  border-left: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Cuando se hace hover sobre el botón de flecha, marcar todo el grupo */
.epc-dropdown-button-group:has(.epc-dropdown-arrow-button:hover) {
  border: 1px solid #00bfff;
}

.epc-dropdown-button-group:has(.epc-dropdown-arrow-button:hover) .epc-dropdown-main-button {
  border-right: 1px solid #00bfff;
  color: #00bfff;
}

.epc-dropdown-button-group:has(.epc-dropdown-arrow-button:hover) .epc-dropdown-arrow-button {
  border-left: 1px solid #00bfff;
  color: #00bfff;
}

/* Fallback para navegadores que no soportan :has() */
.epc-dropdown-arrow-button:hover {
  border-left: 1px solid #00bfff;
  color: #00bfff;
}

/* Usar JavaScript para agregar clase en hover si :has() no está disponible */
.epc-dropdown-button-group.arrow-hover {
  border: 1px solid #00bfff;
}

.epc-dropdown-button-group.arrow-hover .epc-dropdown-main-button {
  border-right: 1px solid #00bfff;
  color: #00bfff;
}

.epc-dropdown-button-group.arrow-hover .epc-dropdown-arrow-button {
  border-left: 1px solid #00bfff;
  color: #00bfff;
}

.epc-dropdown-arrow-button .icon {
  transition: transform 0.2s ease;
  font-size: 10px;
  width: 10px;
  height: 10px;
}

.epc-dropdown-arrow-button .icon.rotate-180 {
  transform: rotate(180deg);
}

.epc-dropdown-menu {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  background-color: rgba(34,34,34,.94);
  box-shadow: 1px 3px 10px 0 rgba(0,0,0,.5);
  border-radius: 5px;
  min-width: 150px;
  z-index: 1000;
  overflow: hidden;
  pointer-events: all;
}

.epc-dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  color: #f4f4f4;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s ease;
}

.epc-dropdown-item:hover {
  background-color: rgba(0, 191, 255, 0.2);
  color: #00bfff;
}

.epc-dropdown-item.active {
  background-color: rgba(0, 191, 255, 0.3);
  color: #00bfff;
}

.epc-dropdown-item .icon {
  width: 14px;
  height: 14px;
}

.epc-button {
  border: 1px solid rgba(34,34,34,0);
  border-radius: 4px;
  cursor: pointer;
  float: left;
  margin: 4px;
  /* padding: 6px; */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.epc-button:hover,
.epc-button.active {
  border: 1px solid #00bfff;
  color: #00bfff;
}

/* Evitar borde completo en el botón de flecha del dropdown */
.epc-dropdown-arrow-button:hover,
.epc-dropdown-arrow-button.active {
  border: none;
  border-left: 1px solid #00bfff;
}
</style>
